import React from 'react'

import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLongArrowLeft } from '@fortawesome/pro-regular-svg-icons/faLongArrowLeft'

import { reverse } from 'src/routes/config'
import useBreadcrumbs from 'src/hooks/useBreadcrumbs'

import styles from './goBackArrow.module.scss'

/**
 * GoBackArrow renders a back button that navigates to the most appropriate
 * previous screen, based on the given props.
 *
 * Priority:
 * 1) If an explicit origin (`from` prop) is provided, navigate to that location.
 * 2) Otherwise, navigate to the previous entry in the browser history.
 * 3) If breadcrumb navigation is explicitly requested (goToBreadcrumb === true)
 *    and a breadcrumb exists, navigate via the breadcrumb.
 * 4) As a final fallback, navigate to the dashboard.
 * @param {object} props.history React Router history object.
 * @param {{ pathname: string, search?: string }} [props.from] Explicit origin (pathname + search) passed via Link state.
 * @param {Function} [props.onClickCallback] Optional callback executed on click
 *        before navigation.
 * @param {boolean} [props.goToBreadcrumb=false] Forces breadcrumb navigation
 *        instead of browser back.
 */
function GoBackArrow({
    history,
    from,
    onClickCallback,
    goToBreadcrumb = false,
}) {
    const { previousBreadcrumb } = useBreadcrumbs()

    const onClick = e => {
        if (onClickCallback) {
            onClickCallback(e)
        }

        if (from?.pathname) {
            history.push(from)
        } else if (!goToBreadcrumb) {
            history.goBack()
        } else if (previousBreadcrumb?.linkName) {
            history.push(reverse(previousBreadcrumb.linkName))
        } else {
            history.push(reverse('dashboard'))
        }
    }

    return (
        <button className={styles.goBack} onClick={onClick}>
            <FontAwesomeIcon icon={faLongArrowLeft} />
        </button>
    )
}
GoBackArrow.propTypes = {
    history: PropTypes.object.isRequired,
    from: PropTypes.shape({
        pathname: PropTypes.string,
        search: PropTypes.string,
    }),
    onClickCallback: PropTypes.func,
    goToBreadcrumb: PropTypes.bool,
}
export default GoBackArrow
